import React, { useEffect, useState, useMemo, ReactNode, useRef } from "react";
import { useLocation } from "react-router-dom";
import { GoogleMap } from "@react-google-maps/api";

interface CloudMapComponentProps {
    lat: number;
    lng: number;
    containerStyle: React.CSSProperties;
    zoom: number;
    minZoom: number;
    zoomControl?: boolean;
    scrollWheelZoom?: boolean;
    children?: ReactNode;
    onMapLoad?: (map: google.maps.Map) => void;
    gestureHandling?: string;
    maxZoom?: number;
    styles?: any[];
}

const CloudMapComponent: React.FC<CloudMapComponentProps> = (props) => {
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const markerRef = useRef<google.maps.Marker | null>(null);
    const location = useLocation();
    const [mapError, setMapError] = useState<string | null>(null);

    const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth <= 1024);

    useEffect(() => {
        const handleResize = () => {
            setIsTablet(window.innerWidth >= 768 && window.innerWidth <= 1024);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const isStayPageTabletSize = useMemo(() => {
        return location.pathname.includes("/trips") && isTablet;
    }, [location, isTablet]);

    // Cleanup marker on unmount
    useEffect(() => {
        return () => {
            if (markerRef.current) {
                markerRef.current.setMap(null);
            }
        };
    }, []);

    const handleMapLoad = (map: google.maps.Map) => {
        // Create marker manually
        if (markerRef.current) {
            markerRef.current.setMap(null); // Remove existing marker
        }
        
        const marker = new google.maps.Marker({
            position: { lat: props.lat, lng: props.lng },
            map: map,
            icon: {
                url: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTYiIGhlaWdodD0iNTYiIHZpZXdCb3g9IjAgMCA4OCA4OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB4PSIxNSIgeT0iMTMiIHdpZHRoPSI1NiIgaGVpZ2h0PSI1NiIgcng9IjI4IiBmaWxsPSIjZmZmIi8+CiAgPGcgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMjcsMjUpIj4KICAgIDxwYXRoIGQ9Ik0yOC4wMDAxIDE0LjQ0NjNWMjYuMDAzOEMyNy45OTk5IDI2LjM2NjMgMjcuOTAxNyAyNi43MjE5IDI3LjcxNTkgMjcuMDMzMkMyNy41MzAxIDI3LjM0NDQgMjcuMjYzNiAyNy41OTk2IDI2Ljk0NDcgMjcuNzcxOEMyNi42NTU3IDI3LjkyODIgMjYuMzMxNSAyOC4wMDgxIDI2LjAwMjkgMjguMDAzOEgxOS45OTU3QzE5LjczMDUgMjguMDAzOCAxOS40NzYyIDI3Ljg5ODQgMTkuMjg4NiAyNy43MTA5QzE5LjEwMTEgMjcuNTIzNCAxOC45OTU3IDI3LjI2OSAxOC45OTU3IDI3LjAwMzhWMjEuMDAyOUMxOC45OTU3IDIwLjczNzYgMTguODkwNCAyMC40ODMzIDE4LjcwMjggMjAuMjk1OEMxOC41MTUzIDIwLjEwODIgMTguMjYxIDIwLjAwMjkgMTcuOTk1NyAyMC4wMDI5SDEzLjk5NTdDMTMuNzMwNSAyMC4wMDI5IDEzLjQ3NjIgMjAuMTA4MiAxMy4yODg2IDIwLjI5NThDMTMuMTAxMSAyMC40ODMzIDEyLjk5NTcgMjAuNzM3NiAxMi45OTU3IDIxLjAwMjlWMjcuMDAzOEMxMi45OTU3IDI3LjI2OSAxMi44OTA0IDI3LjUyMzMgMTIuNzAyOCAyNy43MTA5QzEyLjUxNTMgMjcuODk4NCAxMi4yNjEgMjguMDAzOCAxMS45OTU3IDI4LjAwMzhINi4wMDAzOEM1LjcxNjg0IDI4LjAwNDUgNS40MzY0MSAyNy45NDQ2IDUuMTc3OSAyNy44MjgxQzQuOTE5MzkgMjcuNzExNiA0LjY4ODc2IDI3LjU0MTIgNC41MDE0NyAyNy4zMjgzQzQuMTcyNzIgMjYuOTQ4MSAzLjk5NDQgMjYuNDYwNyA0LjAwMDEzIDI1Ljk1ODFWMTRjMC0wLjI3ODQgMC4wNTg0LTAuNTUzOSAwLjE3MTEtMC44MDg3QzQuMjgzODQgMTIuOTM3MyA0LjQ0ODQ2IDEyLjcwODggNC42NTQ0NyAxMi41MjEyTDE0LjY1MzkgMy40Mjk0M0MxNS4wMjEyIDMuMDkzMDEgMTUuNTAxMiAyLjkwNjQgMTUuOTk5MyAyLjkwNjM2QzE2LjQ5NzQgMi45MDYzMyAxNi45Nzc0IDMuMDkzODggMTcuMzQ0NyAzLjQzMDI0TDI3LjM0NTYgMTIuNTIyMkMyNy41NTE2IDEyLjcwOTkgMjcuNzE2MSAxMi45Mzg0IDI3LjgyODggMTMuMTkzMkMyNy45NDE1IDEzLjQ0OCAyNy45OTk5IDEzLjcyMzUgMjguMDAwMSAxNC4wMDIxVjE0LjQ0NjNaIiBmaWxsPSIjMjIyMjIyIi8+CiAgPC9nPgo8L3N2Zz4K",
                scaledSize: new google.maps.Size(88, 88),
                anchor: new google.maps.Point(44, 44),
            }
        });
        
        markerRef.current = marker;
        
        setMap(map);
        if (props.onMapLoad) {
            props.onMapLoad(map);
        }
    };

    if (mapError) {
        return <div style={props.containerStyle}>{mapError}</div>;
    }

    return (
        <div style={props.containerStyle}>
            <GoogleMap
                mapContainerStyle={props.containerStyle}
                center={{ 
                    lat: isStayPageTabletSize ? props.lat + 0.006 : props.lat, 
                    lng: props.lng 
                }}
                zoom={props.zoom}
                options={{
                    minZoom: props.minZoom,
                    maxZoom: props.maxZoom || 18,
                    disableDefaultUI: true,
                    zoomControl: props.zoomControl || false,
                    scrollwheel: props.scrollWheelZoom,
                    gestureHandling: props.gestureHandling || "greedy",
                    mapId: '33fa3022f2fc97e5'
                }}
                onLoad={handleMapLoad}
            >
            </GoogleMap>
        </div>
    );
};

// Create a context for the map instance
export const MapContext = React.createContext<google.maps.Map | null>(null);

// Hook to use the map instance
export const useMap = () => {
    return React.useContext(MapContext);
};

export default React.memo(CloudMapComponent); 